import React, { useState, useEffect } from "react";
import { Text, View, StyleSheet, Platform } from "react-native";

import fontStyles from "../../../fonts";
import AppTheme from "../../AppTheme";
import { INTEGRATION_KEYS } from "../IntegrationsHelper";
import appStyles from "../../../appStyles";
import ManaButton from "../../common/ManaButton";
import appPages from "../../../../appPages";
import { navigate } from "../../../../helpers/utils";
import IntegrationRuleElement from "./IntegrationRuleElement";
import { MoodColors } from "../../common/mood/MoodsHelper";

export default function IntegrationRuleSummary({
  integration = {},
  controller,
  deleteIntegration = () => {},
  ignoreIntegration = () => {},
}) {
  const [selectedRule] = useState(integration.rule);
  const [summaryText, setSummaryText] = useState(integration.ruleSummaryText);

  const hasRule = integration.rule && integration.element;
  const editButtonText = hasRule ? "Modify Rule" : "Create New Rule";

  useEffect(() => {
    initSummaryText();
  }, [integration]);

  // ------------------------------------- METHODS -------------------------------------
  const editRule = () => {
    if (!hasRule) {
      // Go to mappings page
      navigate(controller, appPages.integrationsMappingPage, "changePage", {
        integrations: [integration],
      });
    } else {
      // Go to rules page
      navigate(controller, appPages.integrationRulesPage, "changePage", {
        selectedIds: [integration.matching_rule],
        tab: "associations",
      });
    }
  };

  const goToMappingPage = () => {
    navigate(controller, appPages.integrationsMappingPage, "changePage", {
      integrations: [integration],
    });
  };

  const onDeleteRule = () => {
    if (!selectedRule) return;

    // Add integration fields to rule model for dialog confirm display
    deleteIntegration({
      ...integration,
      rule: integration.matching_rule
        ? {
            ...selectedRule,
            integrationName: integration?.source?.name,
            id: integration.matching_rule,
          }
        : null,
    });
  };

  const initSummaryText = () => {
    const { website, eventName, channelName, app } = integration;
    const { name } = integration.source || {};

    let ruleSummaryText = "Take all data that";

    // Use rule when existing
    if (integration.rule && integration.rule.name && integration.rule.text) {
      ruleSummaryText = `${ruleSummaryText} ${integration.rule.name} (${integration.rule.text})`;
      setSummaryText(ruleSummaryText);
      return;
    }

    // Defaults
    if (name == INTEGRATION_KEYS.chrome) {
      ruleSummaryText = `${ruleSummaryText} is from this website (${website})`;
    }

    if (name === INTEGRATION_KEYS.google || name === INTEGRATION_KEYS.outlook) {
      ruleSummaryText = `${ruleSummaryText} matches this event (${eventName}) exactly`;
    }

    if (name === INTEGRATION_KEYS.slack) {
      ruleSummaryText = `${ruleSummaryText} is from this channel (${channelName})`;
    }

    if (name === INTEGRATION_KEYS.desktop) {
      ruleSummaryText = `${ruleSummaryText} is from this application (${app})`;
    }

    if (name === INTEGRATION_KEYS.health) {
      ruleSummaryText = `${ruleSummaryText} is from this activity (${integration.name})`;
    }

    setSummaryText(ruleSummaryText);
  };

  return (
    <View style={[styles.container]}>
      {integration.website ? (
        <Text style={appStyles.captionThin} numberOfLines={1}>
          {integration.website}
        </Text>
      ) : null}

      {/* Associated element */}
      {integration.element ? (
        <>
          <View style={styles.rulesContainer}>
            <Text style={[fontStyles.h4, { marginVertical: 4 }]}>
              Your rule:
            </Text>
            <Text style={[fontStyles.caption, styles.summary]}>
              {summaryText}
            </Text>
          </View>

          <Text
            style={[fontStyles.caption, styles.summary, { marginBottom: 4 }]}
          >
            and associate it with:
          </Text>

          <IntegrationRuleElement
            data={integration}
            controller={controller}
            onPress={goToMappingPage}
          />
        </>
      ) : null}

      {/* Actions */}
      <View style={styles.buttonsContainer}>
        <View style={[appStyles.horizontalButtons]}>
          <ManaButton
            text={"Delete Instance"}
            style={[styles.button, { marginRight: 6 }]}
            styleText={[styles.buttonText, { color: MoodColors.VeryBad }]}
            onPress={onDeleteRule}
          />

          <ManaButton
            isMainButton
            text={editButtonText}
            style={styles.button}
            styleText={styles.buttonText}
            onPress={editRule}
          />
        </View>

        <Text
          style={styles.deleteText}
          onPress={() => ignoreIntegration(integration)}
        >
          Or Ignore Data Like This
        </Text>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    paddingLeft: 28,
    paddingRight: 20,
    display: "flex",
    alignItems: "flex-start",
  },
  rulesContainer: {
    overflow: "visible",
    zIndex: 1000,
    width: "100%",
    borderTopColor: AppTheme.borderColor,
    borderTopWidth: 1,
  },
  summary: {
    color: "#8B92A1",
  },

  buttonsContainer: {
    width: "100%",
    display: "flex",
    alignItems: "center",
    marginTop: 12,
  },
  deleteText: {
    fontFamily: Platform.select({
      default: "Montserrat",
      android: "Montserrat-SemiBold",
    }),
    fontWeight: "600",
    fontSize: 12,
    lineHeight: 15,
    letterSpacing: 0.175,
    color: MoodColors.VeryBad,
    textAlign: "center",
    marginTop: 13,
  },
  button: {
    width: 170,
    height: 32,
  },
  buttonText: {
    fontFamily: Platform.select({
      default: "Montserrat",
      android: "Montserrat-SemiBold",
    }),
    fontWeight: "600",
    fontSize: 12,
    lineHeight: 15,
    letterSpacing: 0.175,
  },
});
